import React, { useEffect, useState } from 'react';
import { Sparkles, AlertTriangle, Trash2, Loader2, CheckCircle2, Database } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

/* Sandbox · Seed & Reset
 * ──────────────────────
 * Two big buttons for the principal: populate every page with realistic-but-
 * obviously-fake demo data, then a NUCLEAR one-tap to wipe all of it before
 * launch. Every seeded record has `sandbox: true` so the nuke is surgical
 * and never touches real records.
 */
/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch + seed effects. */
export default function SandboxAdmin() {
  const [status, setStatus] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [nuking, setNuking] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const reload = async () => {
    try {
      const { data } = await api.get('/sandbox/status');
      setStatus(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load sandbox status.');
    }
  };
  useEffect(() => { reload(); }, []);

  const seed = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post('/sandbox/seed');
      toast.success(`Seeded ${data.total} demo records across ${Object.keys(data.created).length} collections.`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Seed failed.');
    } finally {
      setSeeding(false);
    }
  };

  const nuke = async () => {
    if (confirmText.trim().toUpperCase() !== 'DELETE') {
      toast.error('Type DELETE to confirm.');
      return;
    }
    setNuking(true);
    try {
      const { data } = await api.delete('/sandbox/all');
      toast.success(`Nuked ${data.total} sandbox records.`);
      setConfirmText('');
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Nuke failed.');
    } finally {
      setNuking(false);
    }
  };

  const total = status?.total || 0;
  const runs = status?.seed_runs || [];

  return (
    <div className="max-w-4xl space-y-6" data-testid="sandbox-page">
      <header>
        <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5] flex items-center gap-2">
          <Database size={13}/> Sandbox · seed &amp; reset
        </div>
        <h1 className="display text-3xl sm:text-4xl mt-2 font-bold">Drive a full QA loop before launch.</h1>
        <p className="text-sm text-slate-600 dark:text-white/65 mt-2 max-w-2xl">
          One click populates every page — startups, entities, agreements, documents, meetings, tasks,
          rooms, assets, bookings, council decisions — with realistic-but-clearly-marked
          <span className="font-bold"> [Sandbox]</span> demo data. A second one-tap nuke wipes
          every record marked <code className="mono text-[10px] bg-slate-100 dark:bg-white/10 px-1 rounded">sandbox:true</code> in this
          tenant. Real records are never touched.
        </p>
      </header>

      {/* Status card */}
      <div className="ve-card p-5" data-testid="sandbox-status">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5]">Current sandbox footprint</div>
          {status && (
            <button
              onClick={reload}
              className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 hover:text-[#5E1F8B]"
              data-testid="sandbox-refresh"
            >
              Refresh
            </button>
          )}
        </div>
        {!status && (
          <div className="text-sm text-slate-500 dark:text-white/55 flex items-center gap-2">
            <Loader2 size={14} className="animate-spin"/> Loading…
          </div>
        )}
        {status && total === 0 && (
          <div className="text-sm text-slate-500 dark:text-white/55 italic">
            No sandbox records present. Click <span className="font-bold">Seed</span> below to populate every page.
          </div>
        )}
        {status && total > 0 && (
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <div className="display text-4xl font-bold text-[#8B3FC5] tabular-nums">{total}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mt-1">
                Total sandbox records · {runs.length} seed run{runs.length === 1 ? '' : 's'}
              </div>
            </div>
            <div className="space-y-1.5">
              {Object.entries(status.counts).map(([col, n]) => (
                <div key={col} className="flex items-center justify-between gap-2 text-xs" data-testid={`sandbox-count-${col}`}>
                  <span className="text-slate-500 dark:text-white/55 mono">{col}</span>
                  <span className="font-bold text-slate-700 dark:text-white/85 tabular-nums">{n}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Seed card */}
      <div className="ve-card p-5 border-2 border-[#8B3FC5]/40" data-testid="sandbox-seed-card">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-[#FFF8E5] dark:bg-[#3a2f0a]/50 flex items-center justify-center shrink-0">
            <Sparkles size={18} className="text-[#5E1F8B] dark:text-[#8B3FC5]"/>
          </div>
          <div className="flex-1">
            <div className="text-sm font-bold text-slate-800 dark:text-white/90">Seed demo data</div>
            <div className="text-xs text-slate-500 dark:text-white/60 mt-1">
              Creates a fresh batch of <span className="font-bold">~20</span> records across 10+ collections.
              Idempotent — each click adds a new batch tagged with a unique seed-run id you can see above.
            </div>
            <button
              onClick={seed}
              disabled={seeding}
              data-testid="sandbox-seed-btn"
              className="mt-3 inline-flex items-center gap-1.5 px-4 h-10 text-xs uppercase tracking-[0.2em] font-bold rounded-md bg-[#0A0A0A] text-[#8B3FC5] hover:bg-[#1a1a1a] disabled:opacity-40 transition"
            >
              {seeding ? <Loader2 size={13} className="animate-spin"/> : <Sparkles size={13}/>}
              {seeding ? 'Seeding…' : 'Seed demo data'}
            </button>
          </div>
        </div>
      </div>

      {/* Nuke card */}
      <div className="ve-card p-5 border-2 border-rose-300 dark:border-rose-500/40 bg-rose-50/40 dark:bg-rose-900/10" data-testid="sandbox-nuke-card">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center shrink-0">
            <AlertTriangle size={18} className="text-rose-600 dark:text-rose-300"/>
          </div>
          <div className="flex-1">
            <div className="text-sm font-bold text-rose-700 dark:text-rose-300">Nuclear · delete all sandbox data</div>
            <div className="text-xs text-rose-700/80 dark:text-rose-300/80 mt-1">
              Deletes every record marked <code className="mono bg-rose-100 dark:bg-rose-900/30 px-1 rounded">sandbox:true</code> in this tenant.
              Real records (no sandbox marker) are untouched. Run this once before launch.
            </div>
            <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:items-center">
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type DELETE to confirm"
                data-testid="sandbox-nuke-confirm"
                className="text-xs px-3 h-10 rounded-md border border-rose-200 dark:border-rose-500/40 bg-white dark:bg-black/30 text-slate-800 dark:text-white placeholder:text-rose-400/70 focus:outline-none focus:border-rose-500 w-56"
              />
              <button
                onClick={nuke}
                disabled={nuking || confirmText.trim().toUpperCase() !== 'DELETE' || total === 0}
                data-testid="sandbox-nuke-btn"
                className="inline-flex items-center gap-1.5 px-4 h-10 text-xs uppercase tracking-[0.2em] font-bold rounded-md bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                {nuking ? <Loader2 size={13} className="animate-spin"/> : <Trash2 size={13}/>}
                {nuking ? 'Deleting…' : `Delete ${total || 'all'} sandbox record${total === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Safety footer */}
      <div className="rounded-md bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 p-4 flex items-start gap-2">
        <CheckCircle2 size={14} className="text-emerald-600 shrink-0 mt-0.5"/>
        <div className="text-[11px] text-slate-600 dark:text-white/60 leading-snug">
          <span className="font-bold">Safety: </span>
          All sandbox records carry <code className="mono">sandbox:true</code> and a unique <code className="mono">sandbox_seed</code> tag.
          The nuke query is <code className="mono">{`{ sandbox: true, tenant_id: <yours> }`}</code> — it physically cannot touch real records.
        </div>
      </div>
    </div>
  );
}
